import { useEffect } from 'react';
import { useRouter } from 'next/router';

import {
  ATTRIBUTION_COOKIE_EXPIRATION_DAYS,
  ATTRIBUTION_COOKIE_NAME,
} from '#/lib/attribution-constants';
import { useConsentManager } from '#/lib/consent-manager';

interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_id?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
}

/**
 * Extracts a string value from router query if it's a valid string
 */
function getQueryString(
  query: Record<string, string | string[] | undefined>,
  key: string,
): string | undefined {
  const value = query[key];
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * Hook that reads attribution parameters from URL and persists them to a first-party cookie.
 * This ensures consistent attribution for form submissions within a 7-day window.
 * Only sets the cookie after Airgap is initialized and user has consented to Analytics.
 * Removes the cookie if consent is withdrawn.
 */
function useSetAttributionCookie(): void {
  const { query, isReady } = useRouter();
  const { airgap } = useConsentManager();

  const hasAnalyticsConsent =
    airgap?.isConsented(new Set(['Analytics'])) ?? false;

  useEffect(() => {
    if (typeof window === 'undefined' || !isReady || !airgap) {
      return;
    }

    const attributionData: AttributionData = {};

    const attributionKeys: (keyof AttributionData)[] = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_id',
      'utm_term',
      'utm_content',
      'gclid',
      'fbclid',
      'msclkid',
    ];

    for (const key of attributionKeys) {
      const value = getQueryString(query, key);
      if (value) {
        attributionData[key] = value;
      }
    }

    const hasAttributionData = Object.keys(attributionData).length > 0;

    if (hasAttributionData && hasAnalyticsConsent) {
      const cookieValue = JSON.stringify(attributionData);
      const expirationDate = new Date();
      expirationDate.setDate(
        expirationDate.getDate() + ATTRIBUTION_COOKIE_EXPIRATION_DAYS,
      );

      if (typeof cookieStore !== 'undefined') {
        cookieStore
          .set({
            name: ATTRIBUTION_COOKIE_NAME,
            value: cookieValue,
            expires: expirationDate.getTime(),
          })
          .catch((error) => {
            console.error('Failed to set attribution cookie:', error);
          });
      } else {
        document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(cookieValue)}; expires=${expirationDate.toUTCString()}; path=/`;
      }
    } else if (!hasAnalyticsConsent) {
      if (typeof cookieStore !== 'undefined') {
        cookieStore.delete(ATTRIBUTION_COOKIE_NAME).catch((error) => {
          console.error('Failed to remove attribution cookie:', error);
        });
      } else {
        document.cookie = `${ATTRIBUTION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    }
  }, [query, isReady, airgap, hasAnalyticsConsent]);
}

/**
 * Component that sets attribution cookies from URL parameters.
 * Must be placed inside ConsentProvider to access Airgap context.
 */
export function AttributionCookieSetter() {
  useSetAttributionCookie();
  return null;
}
